import Button from 'assets/components/button/Button';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { combineStrings } from 'assets/utils/data/String';
import { get, uniq } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobEmailTemplate from 'models/core/claimJobEmailTemplate/Model.ClaimJobEmailTemplate';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import { getNextClaimJobStatus } from 'module/claims/hooks/useClaimsGlobalStore';
import { EmailAction } from 'module/claims/views/claimEdit/ClaimEdit.Hooks';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  emailTemplates: ClaimJobEmailTemplate[];
  statusInfo: Model.ClaimJobStatusInfo;
  claimJob: ClaimJob;
  disabled?: boolean;
  isStatusChanged?: boolean;
  ownerId: number;
  currentStatus: Model.Enum.ClaimJobStatusCode;
  changeStatus: Hooks.Form.Export<{
    nextStatus: Model.Enum.ClaimJobStatusCode;
    ownerId?: number;
    claimJobEmailTemplate?: Model.IClaimJobEmailTemplate;
  }>;
  setEmailAction: React.Dispatch<React.SetStateAction<EmailAction>>;
};
export default function ClaimJobStatusStateMachine({
  emailTemplates,
  statusInfo: statusInfoProps,
  claimJob,
  isStatusChanged,
  currentStatus,
  changeStatus,
  disabled,
  setEmailAction,
}: Props) {
  const { lang } = getLocales();
  const { currentPermissions, userId, user } = useAuthStore();
  const { currentStatusInfo, nextStatusList, statusTree, statusTranslations } = useMemo(() => {
    const { currentStatusInfo, nextStatusList, statusTranslations } = getNextClaimJobStatus(
      emailTemplates,
      statusInfoProps,
      currentPermissions,
      currentStatus
    );
    const fullStatusList = nextStatusList?.map((it) => it.nextStatus);
    const primaryList = uniq(nextStatusList?.map((it) => it.statusInfo.primary));
    const secondaryList = uniq(nextStatusList?.map((it) => it.statusInfo.secondary));
    const reasonList = uniq(nextStatusList?.map((it) => it.statusInfo.reason));
    const statusTree = primaryList.reduce(
      (dataP, p) => ({
        ...dataP,
        [p]: secondaryList
          .filter((s) => fullStatusList.some((f) => f.indexOf(combineStrings('_', p, s)) === 0))
          .reduce(
            (dataS, s) => ({
              ...dataS,
              [s]: reasonList
                .filter((r) => fullStatusList.some((f) => f.indexOf(combineStrings('_', p, s, r)) === 0))
                .reduce(
                  (dataR, r) => ({
                    ...dataR,
                    [r]: true,
                  }),
                  {}
                ),
            }),
            {}
          ),
      }),
      {}
    );
    return {
      currentStatusInfo,
      nextStatusList,
      statusTree,
      statusTranslations,
    };
  }, [emailTemplates, statusInfoProps, currentStatus, currentPermissions]);

  const [statusInfo, setStatusInfo] = useState<{ primary?: string; secondary?: string; reason?: string }>({});
  useEffect(() => {
    setStatusInfo({});
  }, [currentStatus]);
  const { list, value, key } = useMemo(
    () => ({
      list: !statusInfo.primary
        ? Object.keys(statusTree || {})
        : !statusInfo.secondary
          ? Object.keys(get(statusTree, statusInfo.primary) || {})
          : Object.keys(get(statusTree, `${statusInfo.primary}.${statusInfo.secondary}`) || {}),
      value: !statusInfo.primary
        ? statusInfo.primary
        : !statusInfo.secondary
          ? statusInfo.secondary
          : statusInfo.reason,
      key: !statusInfo.primary ? 'primary' : !statusInfo.secondary ? 'secondary' : 'reason',
    }),
    [statusInfo, statusTree]
  );

  const selected = useMemo(() => {
    return nextStatusList?.find(
      (it) => it.nextStatus === combineStrings('_', statusInfo.primary, statusInfo.secondary, statusInfo.reason)
    );
  }, [statusInfo, nextStatusList]);

  const statusString = combineStrings(
    ' - ',
    statusInfo.primary ? statusTranslations[statusInfo.primary] : undefined,
    statusInfo.secondary ? statusTranslations[statusInfo.secondary] : undefined,
    statusInfo.reason ? statusTranslations[statusInfo.reason] : undefined
  );

  const isNextStatusApprovedToPay =
    nextStatusList?.find((it) => it.nextStatus === ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING)
      ?.nextStatus === ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING;
  const missingPaymentMethods = useMemo(
    () =>
      isNextStatusApprovedToPay &&
      claimJob?.claimJobItems?.some(
        (it) =>
          !(
            it.paymentMethodCode ||
            it.claimJobItemPayee?.payee?.paymentMethodCode ||
            it.claimJobItemPayee?.dealer?.paymentMethodCode ||
            it.claimJobItemPayee?.client?.paymentMethodCode
          )
      ),
    [claimJob?.claimJobItems, nextStatusList]
  );
  const disableDropdownButton = !(currentStatusInfo || list.length) || disabled || missingPaymentMethods;

  return (
    <DropdownButton
      buttonBody={isStatusChanged ? statusString : lang.changeStatus}
      buttonMedia={<Icon class="fas-check-double" />}
      horizontalPosition="auto_fixed"
      verticalPosition="auto_fixed"
      subcomponents={ThemeDropdownButtonPopup}
      disabled={disableDropdownButton}
      tabIndex={null}
      title={<span title={statusString} children={statusString} />}
      htmlElementProps={{ title: missingPaymentMethods ? lang.missingPayeePaymentMethods : lang.changeStatus }}
      dropdownOptions={({ animateOut }) =>
        selected ? (
          <>
            <Button
              onClick={(status) => {
                if (status?.claimJobEmailTemplate) {
                  changeStatus.update({
                    nextStatus: status?.nextStatus,
                    ownerId: status?.ownerId,
                    claimJobEmailTemplate: status?.claimJobEmailTemplate,
                  });
                  setEmailAction('changeStatusSendEmail');
                  animateOut();
                } else {
                  changeStatus.update({
                    nextStatus: status?.nextStatus,
                    ownerId: status?.ownerId,
                    claimJobEmailTemplate: status?.claimJobEmailTemplate,
                  });
                  setEmailAction(null);
                  animateOut();
                  changeStatus.submit();
                }
              }}
              data={{
                nextStatus: selected.nextStatus,
                ownerId: selected.ownershipAllowed ? userId : null,
                claimJobEmailTemplate: selected.emailTemplate ? selected.emailTemplate : null,
                owner: user,
              }}
              render={lang.apply}
              htmlElementProps={{
                title: `${lang.apply}: ${statusString}, ${
                  selected.ownershipAllowed ? lang.youWillKeepOwnership : lang.ownershipWillBeRemoved
                }`,
              }}
              media={selected.ownershipAllowed ? 'fas-user-check' : 'fas-user-times'}
              class={ThemeButtonMenuItem}
              active
            />
            <Button
              onClick={setStatusInfo}
              data={{}}
              render={lang.cancel}
              class={ThemeButtonMenuItem}
              htmlElementProps={{ title: lang.cancel }}
            />
          </>
        ) : (
          <DataMap
            data={list}
            render={({ data: statusPartition }) =>
              statusPartition !== 'undefined' && (
                <Button
                  key={statusPartition}
                  onClick={setStatusInfo}
                  data={{
                    ...statusInfo,
                    [key]: statusPartition,
                  }}
                  render={statusTranslations[statusPartition]}
                  class={ThemeButtonMenuItem}
                  active={statusPartition === value}
                />
              )
            }
          />
        )
      }
    />
  );
}
